<script setup lang="ts">
import { useElementHover } from '@vueuse/core';
import { useTemplateRef } from 'vue';

import GreatestHitsIcon from '@/components/base/assets/GreatestHitsIcon.vue';
import CloseModalButton from '@/components/base/layout/modal/CloseModalButton.vue';
import PopperWrapper from '@/components/base/layout/PopperWrapper.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import { sendButtonClicked } from '@/utils/analytics/buttonClicked';
import { IconSize } from '@/utils/svg';

const { size = 32 } = defineProps<{
  animation?: 'grow' | 'shine';
  popper?: boolean;
  size?: IconSize | [IconSize, IconSize];
  text?: boolean;
}>();

const iconRef = useTemplateRef<HTMLElement>('icon');
const isHovered = useElementHover(iconRef);
</script>

<template>
  <component :is="popper ? PopperWrapper : 'div'" interactive placement="auto" theme="light">
    <template #content="props">
      <div class="flex flex-col gap-1 p-3.5 font-normal max-w-80">
        <div class="flex justify-between">
          <BaseBodyText class="font-semibold">About Greatest Hits</BaseBodyText>
          <CloseModalButton class="w-4 h-4" size="small" @click="props?.close()" />
        </div>
        <SmallBodyText class="text-neutral-600">
          The all-timers, the fan favorites, the forever snacks you'll never forget. From roasted
          nuts to chewy dried fruit and hand-crafted chocolates, these snacks always top the charts
          at Nuts.com.
        </SmallBodyText>
        <RouterLink
          to="/bestsellers/hero"
          @click="sendButtonClicked({ buttonName: 'Shop All Greatest Hits Products' })"
        >
          <SmallBodyText class="font-semibold underline"> Shop All Greatest Hits </SmallBodyText>
        </RouterLink>
      </div>
    </template>
    <component
      :is="popper ? UnstyledButton : 'div'"
      class="flex relative items-center w-fit"
      :class="{ 'animation-shine overflow-hidden': animation === 'shine' }"
    >
      <GreatestHitsIcon class="relative z-10 flex-shrink-0" ref="icon" :size />

      <template v-if="text">
        <div
          v-if="animation === 'grow'"
          class="flex overflow-hidden absolute left-full items-center -ml-2 h-full"
        >
          <SmallBodyText
            class="flex-shrink-0 py-0.5 pr-1 pl-2 font-semibold text-black uppercase whitespace-nowrap rounded transition-all duration-300 ease-in-out override-line-height bg-nuts-amber-400"
            :class="[isHovered ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0']"
          >
            Greatest Hits
          </SmallBodyText>
        </div>

        <SmallBodyText
          v-else
          class="py-0.5 pr-1 pl-2 -ml-2 font-semibold text-black uppercase rounded bg-nuts-amber-400 override-line-height"
        >
          Greatest Hits
        </SmallBodyText>
      </template>
    </component>
  </component>
</template>

<style scoped>
.animation-shine::after {
  animation: shine 3s ease-in-out 0s infinite;
  content: '';
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.7) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  height: 100%;
  left: -50%;
  pointer-events: none;
  position: absolute;
  top: 0;
  transform: skewX(35deg);
  width: 50%;
  z-index: 10;
}

@keyframes shine {
  0% {
    left: -100%;
  }
  100% {
    left: 150%;
  }
}

.override-line-height {
  @apply leading-none lg:leading-none;
  padding-top: 3px; /* Used to center the text vertically */
}
</style>
